import { Fragment } from 'react';
import { History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { groupByPhtDay, phtDayLabel } from '../../lib/phtDay';
import { selectCanLoadMore, selectShowSkeleton, type FeedState } from '../../lib/feedReducer';
import { groupActivityRows } from '../../lib/grouping';
import { ActivityEventRow } from './ActivityEventRow';
import { ActivityFeedSkeleton } from './ActivityFeedSkeleton';
import { ActivityGroupRow } from './ActivityGroupRow';

interface ActivityFeedProps {
  state: FeedState;
  /** True while a mutation's row is still expected — renders the acknowledgement skeleton at the top. */
  awaitingArrival: boolean;
  /** True when the confirm-poll gave up. Offers a manual refresh; never phrased as an error. */
  arrivalTimedOut: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
  /** Injected so a long-lived panel re-labels "Today" across midnight instead of freezing at mount. */
  now: Date;
}

export function ActivityFeed({
  state,
  awaitingArrival,
  arrivalTimedOut,
  onLoadMore,
  onRetry,
  now,
}: ActivityFeedProps) {
  if (selectShowSkeleton(state)) return <ActivityFeedSkeleton />;

  // The error is ADDITIVE — it never replaces rows already on screen. A transient failure while paging
  // should not blank a feed the user was reading, and a retry path is mandatory: without one a single
  // failure freezes the panel until a route change.
  const errorCard = state.error && (
    <div className="mx-3 my-2 rounded-lg border border-destructive/30 bg-destructive/8 p-3">
      <p className="text-xs text-destructive">{state.error}</p>
      <Button variant="ghost" size="sm" className="mt-1.5 h-7 px-2 text-xs" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );

  if (state.items.length === 0) {
    return (
      <div className="flex h-full flex-col">
        {errorCard}
        {!state.error && (
          <EmptyState
            // Copy is verbatim from the handoff, and the two cases are DIFFERENT sentences on purpose: a
            // record with no history and a filter that matched nothing are different facts, and one of them
            // is fixed by clicking All.
            title={
              state.pill === 'all'
                ? 'No activity yet on this record.'
                : 'No events of this type on this record.'
            }
            className="p-6"
          />
        )}
      </div>
    );
  }

  // Runs, not a groupBy — see `groupByPhtDay`. A late-arriving row that reopens an earlier day is meant to
  // be VISIBLE as an oddity, so the same dayKey can legitimately appear twice.
  // ⚠️ Keyed by INDEX for exactly that reason: keying by dayKey would collide on the repeat.
  const groups = groupByPhtDay(state.items);
  const lastGroup = groups.length - 1;

  return (
    <div className="flex h-full flex-col">
      {errorCard}

      {/* The acknowledgement, and the honest version of it: a skeleton row says "something is being
          recorded" within ~100ms without the UI inventing a row that the consumer has not written yet. */}
      {awaitingArrival && (
        <div className="px-3 pt-2">
          <ActivityFeedSkeleton />
        </div>
      )}

      {arrivalTimedOut && (
        <p className="px-3 pt-2 text-[11px] text-muted-foreground">
          Still recording — this can lag a moment.{' '}
          <button
            type="button"
            onClick={onRetry}
            className="underline underline-offset-2 hover:text-foreground"
          >
            Refresh
          </button>
        </p>
      )}

      <ul className="flex-1">
        {groups.map((group, groupIndex) => (
          <Fragment key={`${group.dayKey}-${groupIndex}`}>
            <li
              // `bg-card` alone is not enough to hide scrolled rows behind a sticky header in LIGHT theme,
              // where --card and --background are the same colour — but the panel's own surface IS card, so
              // this matches its parent rather than the page. Verified against all three themes.
              // z-20: the day header outranks anything a row can draw. An evidence block that sticks
              // inside its own scroller must never be able to slide over the date it belongs under.
              className="sticky top-0 z-20 bg-card px-3 py-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground"
            >
              {phtDayLabel(group.dayKey, now)}
            </li>

            {/* Burst grouping runs WITHIN a day, never across one — a group spanning a day boundary would
                sit under a header that is wrong for half of it. `hasMore` is passed only for the last day,
                because only there can a run continue into rows the reader has not fetched. */}
            {groupActivityRows(group.rows, state.hasMore && groupIndex === lastGroup).map(
              (run, runIndex, runs) => {
                const isLastRunOfFeed = groupIndex === lastGroup && runIndex === runs.length - 1;

                if (run.reason === 'single') {
                  return run.rows.map((entry, rowIndex) => (
                    <ActivityEventRow
                      key={entry.id}
                      entry={entry}
                      // The connector stops at the very last row so it does not dangle into the footer —
                      // but it DOES continue across a day header, because the timeline is continuous.
                      showConnector={!(isLastRunOfFeed && rowIndex === run.rows.length - 1)}
                    />
                  ));
                }

                return (
                  <ActivityGroupRow key={run.key} group={run} showConnector={!isLastRunOfFeed} />
                );
              },
            )}
          </Fragment>
        ))}
      </ul>

      {selectCanLoadMore(state) && (
        <div className="px-3 pb-2 pt-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-full text-xs"
            disabled={state.loading.more}
            onClick={onLoadMore}
          >
            {/* No "(N remaining)". That number came from subtracting a loaded count off a total that may
                already be stale, and it was wrong the moment a row arrived between two pages. */}
            {state.loading.more ? 'Loading…' : 'Show more'}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Re-exported so the panel shell can render the same empty icon without importing lucide twice. */
export const FEED_EMPTY_ICON = History;
